import { Box, List, Skeleton } from "@mui/material";
import { cn } from "@utils";
import { getLeftDrawerRootClassName } from "./leftDrawerShared";

interface LeftDrawerSkeletonProps {
  hasBanner?: boolean;
  isCollapsed?: boolean;
}

const DRAWER_ROW_CLASS_NAME =
  "mx-[6px] my-[4px] flex min-w-0 items-center gap-4 rounded-md px-[10px] py-[8px]";
const COLLAPSED_DRAWER_ROW_CLASS_NAME = "justify-center gap-0 px-0";

const DrawerRowSkeleton = ({
  isCollapsed,
  labelWidth = 88,
  testId,
}: {
  isCollapsed: boolean;
  labelWidth?: number | string;
  testId: string;
}) => (
  <div
    className={cn(
      DRAWER_ROW_CLASS_NAME,
      isCollapsed && COLLAPSED_DRAWER_ROW_CLASS_NAME
    )}
    data-testid={testId}
  >
    <Skeleton
      animation="wave"
      className="shrink-0"
      height={20}
      variant="rounded"
      width={20}
    />
    {!isCollapsed ? (
      <Skeleton
        animation="wave"
        height={18}
        sx={{ flexShrink: 0 }}
        variant="rounded"
        width={labelWidth}
      />
    ) : null}
  </div>
);

const DrawerSectionHeaderSkeleton = ({
  isCollapsed,
  testId,
  titleWidth = 64,
}: {
  isCollapsed: boolean;
  testId: string;
  titleWidth?: number | string;
}) => {
  if (isCollapsed) {
    return (
      <Box
        aria-hidden="true"
        className="mx-4 my-3 h-px bg-slate-200"
        data-testid={testId}
      />
    );
  }

  return (
    <div className="px-4 pr-0" data-testid={testId}>
      <div className="flex min-w-0 items-center gap-2">
        <Skeleton
          animation="wave"
          height={20}
          variant="rounded"
          width={titleWidth}
        />
      </div>
    </div>
  );
};

export const LeftDrawerSkeleton = ({
  hasBanner = false,
  isCollapsed = false,
}: LeftDrawerSkeletonProps) => {
  const drawerRootClassName = getLeftDrawerRootClassName(hasBanner);

  return (
    <div
      aria-hidden="true"
      className={`${drawerRootClassName} flex w-full min-h-0 min-w-0 flex-col pt-[8px] pb-[8px]`}
      data-testid="left-drawer-skeleton-root"
    >
      <List disablePadding className="shrink-0">
        <div
          className={cn(
            DRAWER_ROW_CLASS_NAME,
            "min-w-0",
            isCollapsed && COLLAPSED_DRAWER_ROW_CLASS_NAME
          )}
          data-testid="left-drawer-skeleton-org-row"
        >
          <Skeleton
            animation="wave"
            className="shrink-0"
            height={24}
            variant="circular"
            width={24}
          />
          {!isCollapsed ? (
            <div className="flex min-w-0 items-center gap-2">
              <Skeleton
                animation="wave"
                height={18}
                variant="rounded"
                width={96}
              />
              <Skeleton
                animation="wave"
                height={18}
                variant="circular"
                width={18}
              />
            </div>
          ) : null}
        </div>
      </List>

      <List
        disablePadding
        className="shrink-0"
        data-testid="left-drawer-skeleton-platform-section"
      >
        <DrawerRowSkeleton
          isCollapsed={isCollapsed}
          labelWidth={106}
          testId="left-drawer-skeleton-platform-device-library"
        />
        <DrawerRowSkeleton
          isCollapsed={isCollapsed}
          labelWidth={52}
          testId="left-drawer-skeleton-platform-tasks"
        />
        <DrawerRowSkeleton
          isCollapsed={isCollapsed}
          labelWidth={84}
          testId="left-drawer-skeleton-platform-documents"
        />
      </List>

      <List
        disablePadding
        className="shrink-0"
        data-testid="left-drawer-skeleton-tools-section"
      >
        <DrawerSectionHeaderSkeleton
          isCollapsed={isCollapsed}
          testId="left-drawer-skeleton-tools-header"
          titleWidth={40}
        />
        <DrawerRowSkeleton
          isCollapsed={isCollapsed}
          labelWidth={74}
          testId="left-drawer-skeleton-tools-roadmap"
        />
        <DrawerRowSkeleton
          isCollapsed={isCollapsed}
          labelWidth={90}
          testId="left-drawer-skeleton-tools-traceability"
        />
        <DrawerRowSkeleton
          isCollapsed={isCollapsed}
          labelWidth={72}
          testId="left-drawer-skeleton-tools-assistant"
        />
      </List>

      <List
        disablePadding
        className="shrink-0"
        data-testid="left-drawer-skeleton-more-section"
      >
        <DrawerSectionHeaderSkeleton
          isCollapsed={isCollapsed}
          testId="left-drawer-skeleton-more-header"
          titleWidth={44}
        />
        <DrawerRowSkeleton
          isCollapsed={isCollapsed}
          labelWidth={112}
          testId="left-drawer-skeleton-more-documentation"
        />
        <DrawerRowSkeleton
          isCollapsed={isCollapsed}
          labelWidth={78}
          testId="left-drawer-skeleton-more-changelog"
        />
        <DrawerRowSkeleton
          isCollapsed={isCollapsed}
          labelWidth={88}
          testId="left-drawer-skeleton-more-support"
        />
      </List>
    </div>
  );
};
